import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Stack
} from '@mui/material';
import { PayrollSummary } from '../../services/payrollService';
import { payrollService } from '../../services/payrollService';

interface PayrollSummaryCardsProps {
  summary: PayrollSummary | null;
  loading: boolean;
}

export const PayrollSummaryCards: React.FC<PayrollSummaryCardsProps> = ({
  summary,
  loading
}) => {
  if (loading || !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const summaryCards = [
    {
      title: 'Total Payrolls',
      value: summary.totalPayrolls || 0,
      color: 'primary.main',
      subtitle: 'All time'
    },
    {
      title: 'Pending Approvals',
      value: summary.pendingApprovals || 0,
      color: 'warning.main',
      subtitle: 'Requires attention'
    },
    {
      title: 'Ready for Payment',
      value: summary.readyForPayment || 0,
      color: 'success.main',
      subtitle: 'Approved'
    },
    {
      title: 'Total Paid',
      value: summary.totalPaid || 0,
      color: 'info.main',
      subtitle: 'Completed'
    },
    {
      title: 'Gross Pay (Total)',
      value: payrollService.formatCurrency(summary.totalGrossPay || 0),
      color: 'secondary.main',
      subtitle: 'Before deductions'
    },
    {
      title: 'Net Pay (Total)',
      value: payrollService.formatCurrency(summary.totalNetPay || 0),
      color: 'primary.main',
      subtitle: 'After deductions'
    },
    {
      title: 'This Month',
      value: `${summary.thisMonth?.payrolls || 0} payrolls`,
      color: 'success.main',
      subtitle: payrollService.formatCurrency(summary.thisMonth?.netPay || 0)
    },
    {
      title: 'This Week',
      value: `${summary.thisWeek?.payrolls || 0} payrolls`,
      color: 'info.main',
      subtitle: payrollService.formatCurrency(summary.thisWeek?.netPay || 0)
    }
  ];

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 3
      }}>
        {summaryCards.map((card, index) => (
          <Card key={index}>
            <CardContent>
              <Typography variant="h4" color={card.color} fontWeight="bold">
                {card.value}
              </Typography>
              <Typography variant="h6" color="text.primary" gutterBottom>
                {card.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.subtitle}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};
